import { createServerFn } from "@tanstack/react-start";
import * as db from "@/lib/db.server";

const COLS =
  "id, name, specialty, city, state, zip, address, phone, website, rating, google_rating, google_review_count, photos, profile_photo_url, display_photo_url, has_custom_photo, avatar_gender, gender, listing_status, plan, is_featured, featured_subscription_status, availability, available_days, insurance, public_id";

const TTL_MS = 24 * 60 * 60 * 1000; // 24 hours
let cache: { ts: number; rows: any[] } | null = null;
let inflight: Promise<any[]> | null = null;

async function fetchRows(): Promise<any[]> {
  // Try featured providers first. Public read (no auth) — same data the
  // homepage carousel showed before; no per-user scoping needed.
  const featured = await db.query<any>(
    `SELECT ${COLS}
       FROM public.providers
      WHERE is_featured = true
        AND featured_subscription_status = 'active'
        AND lifecycle_status = 'active'
        AND is_deleted = false
      ORDER BY rating DESC
      LIMIT 24`,
  );
  if (featured.length >= 8) return featured;

  // Fallback: real providers, claimed-first, so the carousel is never empty.
  // The old query filtered on google_review_count >= 20 — but only 8 seeded
  // demo rows in the whole 6.18M-row table ever had Google ratings, so it
  // surfaced ONLY those fakes to everyone. Real scraped providers have no
  // rating data at all; the honest ranking signal we DO have is listing_status
  // (claimed/premium listers who engaged), which is exactly what idx_providers_rank
  // orders by. Column list and ORDER mirror that partial index so this stays a
  // bounded 24-row index scan — never the seq-scan+sort that pegged Aurora at
  // 100% ACU (see 2026-06-24-top-rated-index.sql). No listing_status filter in
  // WHERE on purpose: claimed rows sort to the top, and a filter would force a
  // full-index scan hunting for the ~dozen matches instead of stopping at 24.
  const fallback = await db.query<any>(
    `SELECT ${COLS}
       FROM public.providers
      WHERE lifecycle_status = 'active'
        AND is_deleted = false
        AND moderation_status = 'approved'
      ORDER BY (listing_status = 'premium') DESC,
               (listing_status <> 'unclaimed') DESC,
               rating DESC NULLS LAST,
               id
      LIMIT 24`,
  );
  return fallback;
}


// Pulled out as a pure function so it's unit-testable without pulling in
// db.server/createServerFn (see __tests__/top-doctors.functions.test.ts).
export function mergeCityRows(cityRows: any[], nationwideRows: any[]): any[] {
  const seen = new Set(cityRows.map((r) => r.id));
  const filler = nationwideRows.filter((r) => !seen.has(r.id));
  return [...cityRows, ...filler].slice(0, 24);
}

async function getNationwideRows(): Promise<any[]> {
  const now = Date.now();
  if (cache && now - cache.ts < TTL_MS) return cache.rows;

  if (!inflight) {
    inflight = fetchRows()
      .then((rows) => {
        cache = { ts: Date.now(), rows };
        return rows;
      })
      .finally(() => {
        inflight = null;
      });
  }
  try {
    return await inflight;
  } catch (e) {
    // Serve stale on error to keep the carousel populated.
    return cache ? cache.rows : [];
  }
}

export const getTopDoctors = createServerFn({ method: "GET" }).handler(async () => {
  return { rows: await getNationwideRows() };
});

// ponytail: module-level cache per warm Lambda container, same pattern as
// location-counts.functions.ts. Reads ONLY the small precomputed
// top_doctors_by_city table (refreshed nightly, see
// aws-migration/sql/2026-07-08-top-doctors-by-city.sql) — never a live
// filtered scan of the 6M-row providers table, which is what pegged Aurora
// at 100% ACU the last time this exact carousel's query got scoped per request.
// `scoped` = true when the rows are genuinely geo-selected (near the visitor);
// false when we fell back to the nationwide list. The client uses it to avoid
// mislabelling the nationwide list as "near {city}".
type CityEntry = { ts: number; rows: any[]; scoped: boolean };
const cityCache = new Map<string, CityEntry>();

// Column list prefixed for the JOIN, derived once at module load so a COLS
// reformat breaks at boot, not per-request.
const P_COLS = COLS.split(", ").map((c) => `p.${c}`).join(", ");

const num = (v: unknown, lo: number, hi: number): number | null =>
  typeof v === "number" && Number.isFinite(v) && v >= lo && v <= hi ? v : null;

export const getTopDoctorsNearCity = createServerFn({ method: "GET" })
  // Public unauthenticated endpoint: reject non-strings/out-of-range and cap
  // length so a crafted payload can't throw (500) or stuff unbounded cache
  // keys. Normalizes to the form the tables store (lower city, upper state).
  .inputValidator((d: unknown): { city: string; state: string; lat: number | null; lon: number | null } => {
    const o = (d ?? {}) as Record<string, unknown>;
    return {
      city: typeof o.city === "string" ? o.city.trim().toLowerCase().slice(0, 100) : "",
      state: typeof o.state === "string" ? o.state.trim().toUpperCase().slice(0, 2) : "",
      lat: num(o.lat, -90, 90),
      lon: num(o.lon, -180, 180),
    };
  })
  .handler(async ({ data: { city, state, lat, lon } }): Promise<{ rows: any[]; scoped: boolean }> => {
    const hasCoords = lat != null && lon != null;
    if (!hasCoords && (!city || !state)) return { rows: [], scoped: false };

    // Coord key is rounded to ~1km so raw-float coordinates from a public
    // endpoint can't mint unbounded distinct cache keys.
    const key = hasCoords ? `geo:${lat!.toFixed(2)},${lon!.toFixed(2)}` : `city:${city}|${state}`;
    const now = Date.now();
    const hit = cityCache.get(key);
    if (hit && now - hit.ts < TTL_MS) return { rows: hit.rows, scoped: hit.scoped };

    try {
      let cityRows: any[];
      if (hasCoords) {
        // True "near you": GiST-KNN the nearest precomputed cities to the
        // visitor, then pull their top-ranked doctors in (distance, rank)
        // order. Only the tiny top_doctor_cities/top_doctors_by_city tables
        // and PK lookups into providers are touched — never a filtered scan.
        cityRows = await db.query<any>(
          `WITH near AS (
             SELECT city, state, ll_to_earth(lat, lon) AS pt
               FROM public.top_doctor_cities
              -- Cap at ~100 miles: past that it isn't "near you". A visitor with
              -- no covered city inside the radius yields 0 rows here and falls
              -- through to the nationwide list (scoped=false), instead of being
              -- shown doctors hundreds of miles away under a "near {city}" label.
              WHERE earth_distance(ll_to_earth(lat, lon), ll_to_earth($1, $2)) < 160934
              ORDER BY ll_to_earth(lat, lon) <-> ll_to_earth($1, $2)
              LIMIT 12
           )
           SELECT ${P_COLS}
             FROM near n
             JOIN public.top_doctors_by_city t ON t.city = n.city AND t.state = n.state
             JOIN public.providers p ON p.id = t.provider_id
               AND p.lifecycle_status = 'active' AND p.is_deleted = false
               AND p.moderation_status = 'approved'
            ORDER BY (n.pt <-> ll_to_earth($1, $2)), t.rank
            LIMIT 24`,
          [lat, lon],
        );
      } else {
        // No coords (ipapi gave city only): exact normalized-city match,
        // PK-served by top_doctors_by_city (city, state, rank).
        cityRows = await db.query<any>(
          `SELECT ${P_COLS}
             FROM public.top_doctors_by_city t
             JOIN public.providers p ON p.id = t.provider_id
               AND p.lifecycle_status = 'active' AND p.is_deleted = false
               AND p.moderation_status = 'approved'
            WHERE t.city = $1 AND t.state = $2
            ORDER BY t.rank
            LIMIT 24`,
          [city, state],
        );
      }

      // Only claim "near {city}" when the geo rows genuinely make up the list.
      // A handful of local rows padded to 24 with nationwide filler is NOT a
      // local list — labeling it "near you" is misleading. 8 matches the
      // "enough to be a real list" floor used by the nationwide fetch and the
      // carousel's repeat-to-fill threshold.
      const scoped = cityRows.length >= 8;

      // Sparse result — top up with the (nationwide-cached) fallback rather
      // than a half-empty carousel. Topped-up rows aren't "near you"; scoped
      // above already reflects whether the local rows carry the list.
      let rows = cityRows;
      let cacheable = true;
      if (rows.length < 24) {
        const nationwide = await getNationwideRows();
        rows = mergeCityRows(rows, nationwide);
        // Empty nationwide means that fetch failed with no stale copy — don't
        // pin a degraded result for a full TTL.
        cacheable = nationwide.length > 0;
      }

      if (cacheable) {
        cityCache.delete(key); // re-insert so size-cap eviction drops stalest-written first
        cityCache.set(key, { ts: now, rows, scoped });
        if (cityCache.size > 500) {
          const oldestKey = cityCache.keys().next().value;
          if (oldestKey) cityCache.delete(oldestKey);
        }
      }
      return { rows, scoped };
    } catch {
      // Tables missing (migration not applied yet) or transient DB error —
      // return empty so the client keeps the SSR nationwide list; never 500.
      return { rows: [], scoped: false };
    }
  });
